import { cn } from "@lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { ButtonHTMLAttributes, Ref } from "react";
import { cva, type VariantProps } from "class-variance-authority";

const buttonVariants = cva(
	"ring-offset-background focus-visible:ring-ring flex shrink-0 cursor-pointer items-center justify-center gap-1 rounded-xl text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50",
	{
		variants: {
			variant: {
				default: "bg-primary text-primary-foreground hover:bg-primary/90",
				success: "bg-success text-success-foreground hover:bg-success/90",
				warning: "bg-warning text-warning-foreground hover:bg-warning/90",
				destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
				outline: "border border-border bg-background hover:bg-accent hover:text-accent-foreground",
				secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
				ghost: "hover:bg-accent hover:text-accent-foreground",
				link: "text-primary underline-offset-4 hover:underline",
			},
			size: {
				default: "h-10 px-4 py-2",
				sm: "h-8 px-2",
				lg: "h-12 px-6",
				icon: "size-10",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	}
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = ({ ref, className, variant, size, asChild = false, ...props }: ButtonProps & { ref?: Ref<HTMLButtonElement> }) => {
	const Component = asChild ? Slot : "button";
	return <Component className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
};
Button.displayName = "Button";

export { Button, buttonVariants };
